import React, { useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import Layout, { Breadcrumbs } from "../components/Layout";
import Seo from "../components/Seo";
import { getPreviousResults } from "../lib/api";
import { ChevronLeft, ChevronRight } from "lucide-react";

const crumbs = [
  { name: "Home", path: "/" },
  { name: "Previous Result", path: "/previous-result/" },
];

export default function PreviousResult() {
  const [page, setPage] = useState(1);
  const [year, setYear] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["previous-results", page, year],
    queryFn: () =>
      getPreviousResults({ page, limit: 20, year: year || undefined }),
    placeholderData: keepPreviousData,
  });

  const items = data?.items || [];
  const pages = data?.pages || 1;
  const years = data?.years || [];

  return (
    <Layout>
      <Seo
        title="Bhutan Morning Teer Previous Result – 2020 to 2026"
        description="Bhutan Morning Teer Previous Result history from 2020 to 2026. View verified past FR and SR results in a clean, mobile-friendly table."
        path="/previous-result/"
        breadcrumb={crumbs}
      />
      <Breadcrumbs items={crumbs} />

      <div className="mx-auto max-w-3xl px-3 py-4">
        <div className="mb-4 rounded-lg border-2 border-[#1ab5a3] bg-white py-2 text-center">
          <h1 className="text-2xl font-extrabold text-[#10161f] sm:text-3xl">
            PREVIOUS RESULTS
          </h1>
        </div>

        {years.length > 0 && (
          <div className="mb-3 flex items-center gap-2">
            <label htmlFor="year-filter" className="text-sm font-semibold text-slate-600">
              Filter by year:
            </label>
            <select
              id="year-filter"
              data-testid="year-filter"
              value={year}
              onChange={(e) => {
                setYear(e.target.value);
                setPage(1);
              }}
              className="rounded-md border border-slate-300 bg-white px-2 py-1 text-sm"
            >
              <option value="">All</option>
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="overflow-hidden rounded-xl border-2 border-[#1ab5a3] bg-white shadow-sm">
          <table
            data-testid="previous-results-table"
            className="w-full border-collapse text-center"
          >
            <thead>
              <tr className="bg-[#1ab5a3] text-white">
                <th className="border border-[#0f9b8a] px-1 py-2 text-sm font-bold underline sm:text-base">
                  CITY
                </th>
                <th className="border border-[#0f9b8a] px-1 py-2 text-sm font-bold underline sm:text-base">
                  DATE
                </th>
                <th className="border border-[#0f9b8a] px-1 py-2 text-sm font-bold underline sm:text-base">
                  F/R
                </th>
                <th className="border border-[#0f9b8a] px-1 py-2 text-sm font-bold underline sm:text-base">
                  S/R
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={4} className="py-8 text-slate-500">
                    Loading…
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td
                    colSpan={4}
                    data-testid="no-previous-results"
                    className="px-3 py-8 text-sm text-slate-500"
                  >
                    No previous results are available yet. Verified results will
                    appear here once published.
                  </td>
                </tr>
              ) : (
                items.map((r) => (
                  <tr key={r.id} className="odd:bg-white even:bg-[#f5fbfa]">
                    <td className="border border-[#bfe9e3] px-1 py-2 text-xs text-[#10161f] sm:text-base">
                      {r.city}
                    </td>
                    <td className="border border-[#bfe9e3] px-1 py-2 text-xs text-[#10161f] sm:text-base">
                      {r.date}
                    </td>
                    <td className="border border-[#bfe9e3] px-1 py-2 text-lg font-bold text-[#10161f] sm:text-xl">
                      {r.fr || "-"}
                    </td>
                    <td className="border border-[#bfe9e3] px-1 py-2 text-lg font-bold text-[#10161f] sm:text-xl">
                      {r.sr || "-"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {pages > 1 && (
          <div
            data-testid="pagination"
            className="mt-4 flex items-center justify-center gap-3"
          >
            <button
              data-testid="prev-page"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="flex items-center gap-1 rounded-md bg-[#1ab5a3] px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-40"
            >
              <ChevronLeft size={16} /> Prev
            </button>
            <span className="text-sm font-medium text-slate-600">
              Page {page} of {pages}
            </span>
            <button
              data-testid="next-page"
              disabled={page >= pages}
              onClick={() => setPage((p) => Math.min(pages, p + 1))}
              className="flex items-center gap-1 rounded-md bg-[#1ab5a3] px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-40"
            >
              Next <ChevronRight size={16} />
            </button>
          </div>
        )}

        <p className="mt-5 text-center text-xs text-slate-500">
          Historical Bhutan Morning Teer results are shown only when verified
          data is available. Leading zeroes are preserved (e.g. 02, 05).
        </p>
      </div>
    </Layout>
  );
}
